import React, { useState, useRef } from 'react';
import { Button, Text } from '@vibe/core';
import { Info } from '@vibe/icons';
import type { ProgressViewType } from '../../types/timesheet.types';

interface ExplanationContent {
  title: string;
  lines: string[];
}

const EXPLANATIONS: Record<ProgressViewType, ExplanationContent> = {
  progress_timesheet: {
    title: 'Timesheet Progress',
    lines: [
      'Required Hours = user capacity minus holidays and approved leaves for the selected period.',
      'Logged Hours = total worklog hours recorded by each user.',
      'Progress = Logged Hours / Required Hours as a percentage.',
    ],
  },
  progress_utilization: {
    title: 'Resource Utilization',
    lines: [
      'Required Hours = user capacity minus holidays and approved leaves for the selected period.',
      'Planned Hours = total hours from the Original Estimate (or Hours Per Day) column of board items assigned to each user (across all configured boards).',
      'Utilization = Planned Hours / Required Hours as a percentage.',
    ],
  },
  progress_planned_vs_actual: {
    title: 'Planned vs Actual',
    lines: [
      'Planned Hours = total hours from the Original Estimate (or Hours Per Day) column of board items assigned to each user (across all configured boards).',
      'Logged Hours = total worklog hours recorded by each user.',
      'Progress = Logged Hours / Planned Hours as a percentage.',
    ],
  },
};

export interface ProgressExplanationProps {
  progressType: ProgressViewType;
}

const ProgressExplanation: React.FC<ProgressExplanationProps> = ({
  progressType,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const explanation = EXPLANATIONS[progressType];

  return (
    <div className="rp-ts-explanation" ref={buttonRef}>
      <Button
        size={Button.sizes.SMALL}
        kind={Button.kinds.TERTIARY}
        leftIcon={Info}
        onClick={() => setIsOpen(!isOpen)}
      >
        Explanation
      </Button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="rp-ts-explanation__backdrop"
            onClick={() => setIsOpen(false)}
          />
          {/* Popover */}
          <div className="rp-ts-explanation__popover">
            <Text type={Text.types.TEXT1} weight={Text.weights.BOLD}>
              {explanation.title}
            </Text>
            <div className="rp-ts-explanation__content">
              {explanation.lines.map((line, i) => (
                <Text
                  key={i}
                  type={Text.types.TEXT2}
                  style={{ marginTop: i > 0 ? '8px' : '12px' }}
                >
                  {line}
                </Text>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProgressExplanation;
